<script setup lang="ts">
import { localeTag } from '@/i18n'
/**
 * BuildInfoFooter — barra di debug fissa in basso a destra con la versione ATTIVA
 * di frontend e backend, per capire a colpo d'occhio se ciò che gira è aggiornato.
 *
 * Frontend: commit/branch/build-time iniettati a compile-time da vite.config (define).
 *   ⚠️ In `npm run dev` il "build time" è l'orario di AVVIO del dev server e il commit
 *   è quello al momento dell'avvio: dopo un merge va riavviato `npm run dev` per aggiornarli.
 * Backend: GET /api/v2/system/version (commit, orario commit, avvio processo, restartNeeded).
 *   `restartNeeded=true` ⇒ il JAR/processo gira classi più vecchie della compilazione → riavviare.
 *
 * Visibilità: in sviluppo (import.meta.env.DEV) sempre; in produzione SOLO super-admin
 * (l'endpoint BE è già super-admin-only). Chiudibile per la sessione corrente.
 */
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { apiClient } from '@/api/client'

// Interfaccia locale temporanea — allineata a SystemVersionDTO del backend.
// Da sostituire col tipo generato quando /api/v2/system/version sarà in openapi.yaml.
interface SystemVersionResponse {
  startedAt: string
  gitBranch: string
  gitCommit: string
  gitCommitTime: string
  classesCompiledAt: string | null
  restartNeeded: boolean
  springBootVersion?: string | null
  springVersion?: string | null
  hibernateVersion?: string | null
  javaVersion?: string | null
  databaseVersion?: string | null
}

const auth = useAuthStore()

// Costanti FE iniettate da Vite (dichiarate in src/vite-env.d.ts)
const feCommit = __APP_GIT_COMMIT__
const feBranch = __APP_GIT_BRANCH__
const feBuildTime = __APP_BUILD_TIME__
// typeof: se la costante non viene iniettata (configurazione di Vite sbagliata) il riquadro
// deve mostrare «sconosciuta», non rompersi e sparire insieme al resto delle informazioni.
const feVue = typeof __APP_VUE_VERSION__ !== 'undefined' ? __APP_VUE_VERSION__ : 'sconosciuta'
const fePrimeVue = typeof __APP_PRIMEVUE_VERSION__ !== 'undefined' ? __APP_PRIMEVUE_VERSION__ : 'sconosciuta'

const DISMISS_KEY = 'buildInfoFooter:dismissed'
const dismissed = ref(sessionStorage.getItem(DISMISS_KEY) === '1')
const expanded = ref(false)
const loading = ref(false)
const beError = ref<string | null>(null)
const be = ref<SystemVersionResponse | null>(null)

// In dev mostriamo sempre; in prod solo al super-admin (come l'endpoint BE).
const canShow = computed(() => import.meta.env.DEV || auth.isPlatformSuperAdmin)

// I commit combaciano? (stesso repo → short hash uguale). 'unknown' = non confrontabile.
const commitsMatch = computed(() => {
  const b = be.value?.gitCommit
  if (!b || b === 'unknown' || feCommit === 'unknown') return null
  return b === feCommit
})

// Stato sintetico: ok / warn (disallineati o BE da riavviare) / sconosciuto
const status = computed<'ok' | 'warn' | 'unknown'>(() => {
  if (be.value?.restartNeeded) return 'warn'
  if (commitsMatch.value === false) return 'warn'
  if (commitsMatch.value === true) return 'ok'
  return 'unknown'
})

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat(localeTag(), {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Rome',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

async function fetchBackend(): Promise<void> {
  if (!auth.isAuthenticated) {
    beError.value = 'login richiesto'
    return
  }
  loading.value = true
  beError.value = null
  try {
    const res = await apiClient.get<SystemVersionResponse>('/api/v2/system/version')
    be.value = res.data
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status
    beError.value = status === 403 ? 'solo super-admin' : status ? `errore ${status}` : 'non raggiungibile'
  } finally {
    loading.value = false
  }
}

function dismiss(): void {
  dismissed.value = true
  sessionStorage.setItem(DISMISS_KEY, '1')
}

onMounted(() => {
  if (canShow.value) fetchBackend()
})
</script>

<template>
  <div
    v-if="canShow && !dismissed"
    class="bif"
    :class="`bif--${status}`"
    data-sezione="build-info-footer"
  >
    <button
      class="bif-summary"
      type="button"
      :title="expanded ? 'Comprimi' : 'Dettagli versione'"
      @click="expanded = !expanded"
    >
      <i class="pi pi-wrench bif-icon" />
      <span class="bif-pair"><b>FE</b> <code>{{ feCommit }}</code></span>
      <span class="bif-sep">·</span>
      <span class="bif-pair">
        <b>BE</b>
        <code v-if="be">{{ be.gitCommit }}</code>
        <em v-else class="bif-muted">{{ beError ?? '…' }}</em>
      </span>
      <i
        class="pi"
        :class="status === 'ok' ? 'pi-check-circle' : status === 'warn' ? 'pi-exclamation-triangle' : 'pi-question-circle'"
      />
    </button>

    <div
      v-if="expanded"
      class="bif-details"
    >
      <div class="bif-col">
        <div class="bif-col-title">
          <i class="pi pi-desktop" /> Frontend
        </div>
        <div><span>Branch</span><code>{{ feBranch }}</code></div>
        <div><span>Commit</span><code>{{ feCommit }}</code></div>
        <div><span>Build / avvio dev</span>{{ formatDate(feBuildTime) }}</div>
        <div><span>Vue</span><code>{{ feVue }}</code></div>
        <div><span>PrimeVue</span><code>{{ fePrimeVue }}</code></div>
      </div>

      <div class="bif-col">
        <div class="bif-col-title">
          <i class="pi pi-server" /> Backend
          <button
            class="bif-refresh"
            type="button"
            :disabled="loading"
            title="Aggiorna"
            @click="fetchBackend"
          >
            <i class="pi pi-refresh" :class="{ 'bif-spin': loading }" />
          </button>
        </div>
        <template v-if="be">
          <div><span>Branch</span><code>{{ be.gitBranch }}</code></div>
          <div><span>Commit</span><code>{{ be.gitCommit }}</code></div>
          <div><span>Commit del</span>{{ formatDate(be.gitCommitTime) }}</div>
          <div><span>Avvio server</span>{{ formatDate(be.startedAt) }}</div>
          <div><span>Classi compilate</span>{{ formatDate(be.classesCompiledAt) }}</div>
          <div v-if="be.springBootVersion"><span>Spring Boot</span><code>{{ be.springBootVersion }}</code></div>
          <div v-if="be.javaVersion"><span>Java</span><code>{{ be.javaVersion }}</code></div>
          <!-- Sempre visibile: «non leggibile» dice qualcosa anche lui, sul database o sul backend. -->
          <div><span>Database</span><code>{{ be.databaseVersion ?? 'non leggibile' }}</code></div>
          <div
            v-if="be.restartNeeded"
            class="bif-alert"
          >
            <i class="pi pi-exclamation-triangle" /> Backend: classi ricompilate dopo l'avvio — riavviare
          </div>
        </template>
        <div
          v-else
          class="bif-muted"
        >
          {{ loading ? 'Caricamento…' : `Backend non disponibile (${beError ?? '—'})` }}
        </div>
      </div>

      <div
        v-if="commitsMatch === false"
        class="bif-alert bif-alert--full"
      >
        <i class="pi pi-exclamation-triangle" /> FE e BE su commit diversi — uno dei due non è aggiornato
      </div>

      <button
        class="bif-close"
        type="button"
        title="Nascondi per questa sessione"
        @click="dismiss"
      >
        <i class="pi pi-times" /> Nascondi
      </button>
    </div>
  </div>
</template>

<style scoped>
.bif {
  position: fixed;
  /* Spostato a sinistra per non finire sotto il FAB «Utilità» (angolo in basso a destra). */
  right: 5.25rem;
  bottom: 0.75rem;
  z-index: 9999;
  font-size: 0.72rem;
  background: var(--surf-card);
  border: 1px solid var(--p-content-border-color);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  max-width: min(92vw, 360px);
}

/* Bordo sinistro colorato come stato */
.bif--ok { border-left: 4px solid var(--p-green-500, #22c55e); }
.bif--warn { border-left: 4px solid var(--p-amber-500, #f59e0b); }
.bif--unknown { border-left: 4px solid var(--p-surface-400, #9ca3af); }

.bif-summary {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.4rem 0.6rem;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--p-text-color);
  font: inherit;
}

.bif-icon { color: var(--p-text-muted-color); }
.bif--warn .bif-icon { color: var(--p-amber-600, #d97706); }

.bif-pair { display: inline-flex; align-items: center; gap: 0.25rem; white-space: nowrap; }
.bif-sep { color: var(--p-text-muted-color); }
.bif-muted { color: var(--p-text-muted-color); font-style: italic; }

.bif code {
  font-family: monospace;
  background: var(--surf-muted);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
}

.bif-details {
  border-top: 1px solid var(--p-content-border-color);
  padding: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.bif-col-title {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

.bif-col > div {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 0.6rem;
  padding: 0.05rem 0;
}

.bif-col > div > span { color: var(--p-text-muted-color); white-space: nowrap; }

.bif-refresh {
  margin-left: auto;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--p-text-muted-color);
  padding: 0;
}

.bif-spin { animation: bif-rot 0.8s linear infinite; }
@keyframes bif-rot { to { transform: rotate(360deg); } }
:global(.app-dark) .bif-alert { color: var(--p-amber-300); }

.bif-alert {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--surf-accent-warn);
  color: var(--p-amber-700, #b45309);
  border: 1px solid var(--p-amber-300, #fcd34d);
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  font-weight: 600;
}

.bif-alert--full { margin-top: -0.1rem; }

.bif-close {
  align-self: flex-end;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--p-text-muted-color);
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}
</style>
